import { motion } from "framer-motion";
import { Cloud, Shield, Bot, BarChart3, ArrowUpRight, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/i18n/useTranslation";
import { useNavigate } from "react-router-dom";

const serviceIcons = [Cloud, Bot, Shield, BarChart3];

const ease = [0.16, 1, 0.3, 1] as const;

const ServicesSection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <section id="services" className="section-padding bg-background">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <p className="label-text mb-3">{t.services.label}</p>
          <h2 className="font-display font-bold text-foreground mb-4">
            {t.services.heading}
          </h2>
          <p className="text-muted-foreground leading-relaxed font-body">
            {t.services.subheading}
          </p>
        </motion.div>

        {/* Flagship card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.6, ease }}
          className="mb-6"
        >
          <div
            className="relative bg-card border-2 border-primary/30 rounded-xl p-8 md:p-10 glow-border overflow-hidden"
            style={{ boxShadow: "var(--accent-glow)" }}
          >
            <div className="absolute top-0 right-0 w-64 h-64 opacity-[0.04]" style={{
              background: "radial-gradient(circle, hsl(24 94% 53%), transparent 70%)",
            }} />
            <div className="relative z-10 flex flex-col md:flex-row md:items-center gap-6">
              <div className="w-14 h-14 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
                <AlertTriangle size={28} className="text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-display font-bold text-foreground text-xl md:text-2xl mb-2">
                  {t.services.flagship.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed font-body max-w-2xl">
                  {t.services.flagship.desc}
                </p>
              </div>
              <Button
                variant="premium"
                size="lg"
                className="shrink-0 self-start md:self-center"
                onClick={() => navigate("/compliance-reality-check")}
              >
                {t.services.flagship.cta}
                <ArrowUpRight size={16} />
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Supporting pillars */}
        <div className="grid sm:grid-cols-2 gap-4">
          {t.services.items.map((s, i) => {
            const Icon = serviceIcons[i];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30, scale: 0.97 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.07, ease }}
                whileHover={{ y: -4, transition: { duration: 0.25 } }}
                className="group relative bg-card border border-border rounded-xl p-7 glow-border"
                style={{ boxShadow: "var(--shadow-card)" }}
              >
                <div className="flex items-start justify-between mb-5">
                  <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Icon size={22} className="text-primary" />
                  </div>
                  <ArrowUpRight
                    size={16}
                    className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                </div>
                <h3 className="font-display font-semibold text-foreground text-lg mb-2">
                  {s.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed font-body">
                  {s.desc}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
